import TelemetryButton from "@/design-system/telemetry-button";
import type { IImageLoader } from "@Components/ImageLoader";
import ImageLoader from "@Components/ImageLoader";
import type { ILink } from "@Components/Link";
import * as stylex from "@stylexjs/stylex";

import { ComposableSpacing, type QueryMdMin } from "../../../styles/tokens.stylex";
import { InfoLabel } from "../../shared/InfoLabel";

export interface IFlyerSectionItem {
    id: string;
    flyerImage: IImageLoader;
    link: ILink;
    newFlyerLabel: string;
    validityTitle: string;
    brandName: string;
    flyerSeoDescription: string;
    isNewFlyer: boolean;
    isCenterItem: boolean;
    isHidden?: boolean;
    isMobile?: boolean;
}

export function FlyerSectionItem({
    id,
    link,
    flyerImage,
    newFlyerLabel,
    isNewFlyer,
    flyerSeoDescription,
    isHidden,
    isMobile,
}: IFlyerSectionItem) {
    return (
        <TelemetryButton
            as="a"
            variant="none"
            size="None"
            href={link.navigateUrl}
            eventName="common-flyerClick"
            itemId={id}
            styleX={styles.flyerSectionItemComponent}
            aria-label={flyerSeoDescription}
            aria-hidden={isHidden}
        >
            <ImageLoader
                {...flyerImage}
                styleX={styles.image}
                size={isMobile ? 300 : 600}
                srcSetAxis="width"
                alt={flyerSeoDescription}
                lazyLoading
                bypassFormats
            />
            {isNewFlyer && (
                <InfoLabel
                    iconType="Star"
                    text={newFlyerLabel}
                    labelType="green"
                    styleX={[styles.infoLabel, styles.newFlyerLabel]}
                />
            )}
        </TelemetryButton>
    );
}

const mdMin: QueryMdMin = "@media (min-width: 990px)";

const styles = stylex.create({
    flyerSectionItemComponent: {
        borderRadius: "8px",
        boxShadow: "inset 0 0 25px rgba(0, 0, 0, 0.2)",
        display: "block",
        position: "relative",
        userSelect: "none",
        height: "100%",

        "::after": {
            backgroundImage:
                "radial-gradient(ellipse at center, rgba(128, 128, 128, 0.8) 0%, rgba(153, 153, 153, 0.8) 20%, rgba(186, 186, 186, 0.45) 45%, rgba(255, 255, 255, 0) 75%);",
            content: "",
            display: "block",
            position: "absolute",
            bottom: "-30px",
            height: "20px",
            width: "100%",
        },
    },

    image: {
        borderRadius: "8px",
        maxWidth: { default: null, [mdMin]: "300px" },
    },

    infoLabel: {
        opacity: 0,
        position: "absolute",
        transform: "translate(-15px, 0) scale(.6)",
        transitionDuration: "1.4s",
        transitionProperty: "all",
        transitionTimingFunction: "ease-out",
        zIndex: 1,
        bottom: ComposableSpacing.s1,
        left: 0,
    },

    newFlyerLabel: {
        opacity: 1,
    },
});
